import AddCircleIcon from '@mui/icons-material/AddCircle';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import ListIcon from '@mui/icons-material/List';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Alert, Box, CircularProgress, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import useApiHook from "../../hooks/useApiHook";
import { useCreatePatientModal, useDeletePatientModal, usePatientRefresh, useRenamePatientModal } from "../../store/modal-context";
import PatientListBase from "./PatientListBase";

const defaultShortcutItems = [
    { id: 'dashboard', title: "Tutti i Pazienti", link: "/dashboard", icon: <ListIcon /> },
    // { id: 'no-patient', title: "Senza paziente", link: "/patient/noPatient", icon: <PersonOffOutlinedIcon /> }
];

const PatientBrowser = ({
    isActive = true,
    showShortcuts = false,
    showCreateAction = true,
    createActionPlacement = 'list',
    selectionMode = 'none',
    selectedPatientId,
    selectedPatientIds,
    onSelect = () => { },
    onPatientClick = null,
    getPatientLink = null,
    shortcutItems = defaultShortcutItems,
    getShortcutLink = (item) => item.link,
    createPatientModalPayload = null,
    boxed = false,
    compactList = false,
    width = '100%',
    mt = 0,
}) => {
    const createPatientModal = useCreatePatientModal();
    const renamePatientModal = useRenamePatientModal();
    const deletePatientModal = useDeletePatientModal();
    const { response, makeRequest, loading, error } = useApiHook();
    const location = useLocation();
    const { patientsVersion } = usePatientRefresh();
    const [internalSelectedPatientId, setInternalSelectedPatientId] = useState(selectedPatientId ?? null);
    const [internalSelectedPatientIds, setInternalSelectedPatientIds] = useState(Array.isArray(selectedPatientIds) ? selectedPatientIds : []);
    const [patientMenuAnchorEl, setPatientMenuAnchorEl] = useState(null);
    const [patientMenuItem, setPatientMenuItem] = useState(null);

    useEffect(() => {
        if (selectedPatientId !== undefined) {
            setInternalSelectedPatientId(selectedPatientId ?? null);
        }
    }, [selectedPatientId]);

    useEffect(() => {
        if (selectedPatientIds !== undefined) {
            setInternalSelectedPatientIds(Array.isArray(selectedPatientIds) ? selectedPatientIds : []);
        }
    }, [selectedPatientIds]);

    useEffect(() => {
        if (!isActive) {
            return;
        }

        makeRequest('/folder/getAll');
    }, [patientsVersion, isActive, location.pathname, makeRequest]);

    const currentSelectedPatientId = selectedPatientId !== undefined ? (selectedPatientId ?? null) : internalSelectedPatientId;
    const currentSelectedPatientIds = useMemo(() => {
        if (selectedPatientIds !== undefined) {
            return Array.isArray(selectedPatientIds) ? selectedPatientIds : [];
        }

        return internalSelectedPatientIds;
    }, [internalSelectedPatientIds, selectedPatientIds]);

    const patientItems = useMemo(() => {
        if (!response?.data || !Array.isArray(response.data)) {
            return [];
        }

        return response.data.map((patient) => ({
            id: patient.id,
            title: patient.name,
            icon: <FolderSharedOutlinedIcon />
        }));
    }, [response]);

    const normalizedSelectedPatientIds = useMemo(
        () => Array.isArray(currentSelectedPatientIds) ? currentSelectedPatientIds : [],
        [currentSelectedPatientIds]
    );

    const onPatientItemClickHandler = (item) => {
        if (selectionMode === 'multiple') {
            const nextSelectedPatientIds = normalizedSelectedPatientIds.includes(item.id)
                ? normalizedSelectedPatientIds.filter((id) => id !== item.id)
                : [...normalizedSelectedPatientIds, item.id];

            if (selectedPatientIds === undefined) {
                setInternalSelectedPatientIds(nextSelectedPatientIds);
            }

            onSelect(item, nextSelectedPatientIds);
            return;
        }

        if (selectionMode === 'single') {
            const nextSelectedPatientId = currentSelectedPatientId === item.id ? null : item.id;

            if (selectedPatientId === undefined) {
                setInternalSelectedPatientId(nextSelectedPatientId);
            }

            onSelect(item, nextSelectedPatientId);
            return;
        }

        if (onPatientClick) {
            onPatientClick(item);
        }
    };

    const onCreatePatientHandler = () => {
        createPatientModal.openModal(createPatientModalPayload);
    };

    const openPatientMenuHandler = (event, item) => {
        event.preventDefault();
        event.stopPropagation();
        setPatientMenuAnchorEl(event.currentTarget);
        setPatientMenuItem(item);
    };

    const closePatientMenuHandler = () => {
        setPatientMenuAnchorEl(null);
        setPatientMenuItem(null);
    };

    const renamePatientClickHandler = () => {
        if (!patientMenuItem) return;
        renamePatientModal.openModal({
            patientId: patientMenuItem.id,
            patientName: patientMenuItem.title,
        });
        closePatientMenuHandler();
    };

    const deletePatientClickHandler = () => {
        if (!patientMenuItem) return;
        deletePatientModal.openModal({
            patientId: patientMenuItem.id,
            patientName: patientMenuItem.title,
        });
        closePatientMenuHandler();
    };

    const content = (
        <>
            {showShortcuts && (
                <>
                    <Typography variant="h6" fontWeight={'bold'}>
                        Gestione Pazienti
                    </Typography>
                    <PatientListBase
                        items={shortcutItems}
                        getItemLink={getShortcutLink}
                    />
                </>
            )}

            {loading && isActive ? (
                <Stack alignItems="center" py={4}>
                    <CircularProgress />
                </Stack>
            ) : null}

            {!loading && error && isActive ? (
                <Alert severity="error">
                    Impossibile caricare i pazienti.
                </Alert>
            ) : null}

            {!loading && !error && isActive && patientItems.length === 0 ? (
                <Stack alignItems="center" py={3}>
                    <Typography variant="body2" sx={{ opacity: 0.65 }}>
                        Nessun paziente.
                    </Typography>
                </Stack>
            ) : null}

            {showCreateAction && createActionPlacement === 'list' && (
                <ListItem disablePadding>
                    <ListItemButton onClick={onCreatePatientHandler}>
                        <ListItemIcon sx={{ minWidth: '40px', position: 'relative' }}>
                            <Stack width={24} height={24} justifyContent="center" alignItems="center" position="relative">
                                <AddCircleIcon
                                    sx={{
                                        position: 'absolute',
                                        top: -6,
                                        left: 12,
                                        zIndex: 1,
                                        fontSize: '0.95rem',
                                        color: '#4f8df7',
                                        backgroundColor: '#fff',
                                        borderRadius: '50%'
                                    }}
                                />
                                <PersonOutlineIcon
                                    sx={{
                                        position: 'absolute',
                                        fontSize: '1.55rem',
                                        color: '#f6bf45'
                                    }}
                                />
                            </Stack>
                        </ListItemIcon>
                        <ListItemText
                            primary="Crea Paziente"
                            sx={{
                                color: 'black',
                                my: 0
                            }}
                            primaryTypographyProps={{
                                sx: {
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    lineHeight: 1.1
                                }
                            }}
                        />
                    </ListItemButton>
                </ListItem>
            )}

            {!loading && !error && patientItems.length > 0 ? (
                <PatientListBase
                    items={patientItems}
                    selectedItemId={selectionMode === 'single' ? currentSelectedPatientId : null}
                    selectedItemIds={selectionMode === 'multiple' ? normalizedSelectedPatientIds : []}
                    onItemClick={selectionMode !== 'none' || onPatientClick ? onPatientItemClickHandler : null}
                    getItemLink={selectionMode === 'none' ? getPatientLink : null}
                    compact={compactList}
                    renderItemActions={selectionMode === 'none' ? (item) => (
                        <IconButton
                            size="small"
                            aria-label={`azioni ${item.title}`}
                            onClick={(event) => openPatientMenuHandler(event, item)}
                            sx={{ ml: 1 }}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    ) : null}
                />
            ) : null}

            <Menu
                anchorEl={patientMenuAnchorEl}
                open={Boolean(patientMenuAnchorEl)}
                onClose={closePatientMenuHandler}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={renamePatientClickHandler}>Rinomina Paziente</MenuItem>
                <MenuItem onClick={deletePatientClickHandler}>Elimina Paziente</MenuItem>
            </Menu>

        </>
    );

    return (
        <Stack direction="column" spacing={2} mt={mt} width={width}>
            {boxed ? (
                <Box border="1px solid #d9d9d9" borderRadius={1} p={1} width="100%" maxWidth="370px">
                    {content}
                </Box>
            ) : (
                content
            )}
        </Stack>
    );
};

export default PatientBrowser;
